import { Radar } from 'lucide-react';
import { type ChangeEvent, memo, useCallback } from 'react';

import { useIntl } from '../../hooks';
import { Button, Input } from '../ui';

type SuppressDetectionGroupActionProps = {
  durationValue: string;
  disabled: boolean;
  fieldDisabled?: boolean;
  durationLabel: string;
  buttonLabel: string;
  maxDuration: number;
  onDurationChange: (value: string) => void;
  bandwidthValueMhz?: string;
  onBandwidthChange?: (value: string) => void;
  bandwidthOptions?: readonly number[];
  bandwidthLabel?: string;
};

const DIGITS_ONLY = /^\d*$/;

export const SuppressDetectionGroupAction = memo(function SuppressDetectionGroupAction({
  durationValue,
  disabled,
  fieldDisabled,
  durationLabel,
  buttonLabel,
  maxDuration,
  onDurationChange,
  bandwidthValueMhz,
  onBandwidthChange,
  bandwidthOptions,
  bandwidthLabel,
}: SuppressDetectionGroupActionProps) {
  const intl = useIntl();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (!DIGITS_ONLY.test(value)) return;
      onDurationChange(value);
    },
    [onDurationChange],
  );

  const handleBandwidthChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => onBandwidthChange?.(event.target.value),
    [onBandwidthChange],
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => e.target.select(), []);

  const durationNumber = Number.parseInt(durationValue, 10);
  const exceedsMaxDuration = durationNumber > maxDuration;
  const isDurationValid =
    Number.isFinite(durationNumber) && durationNumber > 0 && !exceedsMaxDuration;
  const isDisabled = disabled || !isDurationValid;
  const durationError = exceedsMaxDuration
    ? intl.formatMessage({ id: 'validation.suppression.durationMax' }, { max: maxDuration })
    : durationValue !== '' && Number.isFinite(durationNumber) && durationNumber < 1
      ? intl.formatMessage({ id: 'validation.suppression.durationMin' })
      : null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {bandwidthOptions && bandwidthOptions.length > 0 && (
          <div className="flex items-center gap-1">
            <Radar aria-hidden className="h-4 w-4 shrink-0 text-foreground/60" />
            <select
              name="bandwidth"
              value={bandwidthValueMhz}
              onChange={handleBandwidthChange}
              disabled={fieldDisabled ?? disabled}
              aria-label={bandwidthLabel}
              className="h-9 rounded-md border border-border bg-background-light px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {bandwidthOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <span className="text-sm text-foreground/70">
              {intl.formatMessage({ id: 'detections.table.mhzSuffix' })}
            </span>
          </div>
        )}
        <Input
          type="number"
          min={1}
          max={maxDuration}
          name="duration"
          value={durationValue}
          onChange={handleChange}
          onFocus={handleFocus}
          className="w-20 h-9 rounded-md border border-border px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          suffix={intl.formatMessage({ id: 'detections.table.secondsSuffix' })}
          aria-label={durationLabel}
          aria-invalid={durationError ? true : undefined}
        />
        <Button
          type="submit"
          variant="destructive"
          size="sm"
          disabled={isDisabled}
          className="text-xs"
        >
          {buttonLabel}
        </Button>
      </div>
      {durationError && <p className="text-xs text-destructive">{durationError}</p>}
    </div>
  );
});
